import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import { baseUrl } from "../../config/baseUrl";
import { toast } from "react-toastify";

const formatPlans = (plans = []) => {
  if (!Array.isArray(plans) || !plans.length) {
    return "-";
  }

  return plans
    .map((plan) => `${plan.validity}M: Rs.${plan.offerPrice || plan.regularPrice}`)
    .join(", ");
};

const getDescriptionPreviewHtml = (description = "") => {
  if (!description?.trim()) {
    return "";
  }

  return DOMPurify.sanitize(description, {
    USE_PROFILES: { html: true },
  });
};

export default function DailyMockCategoryList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sequenceLoading, setSequenceLoading] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/daily/mock/category`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });

      const sortedCategories = [...(response?.data?.data || [])].sort(
        (firstCategory, secondCategory) =>
          (firstCategory.sequence || 0) - (secondCategory.sequence || 0),
      );
      setCategories(sortedCategories);
    } catch (error) {
      console.error("Error fetching daily mock categories:", error);
      toast.error("Failed to fetch daily mock categories");
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    try {
      const response = await axios.patch(
        `${baseUrl}/daily/mock/category/status/${id}`,
        { status: newStatus },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      );

      if (!response?.data?.status) {
        toast.error(response?.data?.message || "Failed to update status");
        return;
      }

      setCategories((previousCategories) =>
        previousCategories.map((category) =>
          category._id === id ? { ...category, status: newStatus } : category,
        ),
      );
      toast.success("Status changed successfully");
    } catch (error) {
      console.error("Error updating daily mock category status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await axios.delete(`${baseUrl}/daily/mock/category/${id}`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });

      if (!response?.data?.status) {
        toast.error(response?.data?.message || "Failed to delete category");
        return;
      }

      toast.success("Category deleted successfully");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting daily mock category:", error);
      toast.error(error?.response?.data?.message || "Failed to delete category");
    }
  };

  const handleSequenceChange = async (categoryId, direction) => {
    const currentIndex = categories.findIndex((category) => category._id === categoryId);

    if (
      (direction === "up" && currentIndex === 0) ||
      (direction === "down" && currentIndex === categories.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const targetSequence = targetIndex + 1;

    setSequenceLoading(categoryId);

    try {
      const reorderedCategories = [...categories];
      const currentCategory = reorderedCategories[currentIndex];
      reorderedCategories[currentIndex] = reorderedCategories[targetIndex];
      reorderedCategories[targetIndex] = currentCategory;

      setCategories(
        reorderedCategories.map((category, index) => ({
          ...category,
          sequence: index + 1,
        })),
      );

      const response = await axios.post(
        `${baseUrl}/daily/mock/category/update-sequence`,
        {
          categoryId,
          targetSequence,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      );

      if (!response?.data?.status) {
        throw new Error(response?.data?.message || "Failed to update sequence");
      }

      toast.success("Sequence updated successfully");
    } catch (error) {
      console.error("Error updating daily mock category sequence:", error);
      toast.error(error?.response?.data?.message || "Failed to update sequence");
      fetchCategories();
    } finally {
      setSequenceLoading(null);
    }
  };

  return (
    <section className="main-sec">
      <div className="row">
        <div className="col-lg-6">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-folder-open me-2" />
              Daily Mock Categories
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Daily Mock Categories
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="col-lg-6 text-end">
          <Link to="/daily-mock-categories/create" className="btn py-2 px-5 text-white btn-for-add">
            <i className="fa fa-plus me-2"></i>
            Add Category
          </Link>
        </div>
        <div className="col-lg-12">
          <div className="cards bus-list">
            <div className="bus-filter">
              <div className="row">
                <div className="col-lg-6">
                  <h5 className="card-title">Daily Mock Category List</h5>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table table-responsive custom-table">
                <table className="table table-borderless">
                  <thead>
                    <tr>
                      <th>SN</th>
                      <th>Sequence</th>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Plans</th>
                      <th>Tests</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          No daily mock categories found.
                        </td>
                      </tr>
                    ) : (
                      categories.map((category, index) => (
                        <tr key={category?._id}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="sequence-controls">
                              <button
                                type="button"
                                className={`sequence-btn ${index === 0 ? "disabled" : ""}`}
                                onClick={() => handleSequenceChange(category._id, "up")}
                                disabled={index === 0 || sequenceLoading === category._id}
                              >
                                <i className="fa fa-arrow-up"></i>
                              </button>
                              <span className="sequence-number">{category?.sequence || index + 1}</span>
                              <button
                                type="button"
                                className={`sequence-btn ${index === categories.length - 1 ? "disabled" : ""}`}
                                onClick={() => handleSequenceChange(category._id, "down")}
                                disabled={index === categories.length - 1 || sequenceLoading === category._id}
                              >
                                <i className="fa fa-arrow-down"></i>
                              </button>
                            </div>
                          </td>
                          <td>
                            {category?.image ? (
                              <img
                                src={`${baseUrl}/${category.image}`}
                                alt={category?.title || "daily mock category"}
                                style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "8px" }}
                                onError={(event) => {
                                  event.target.style.display = "none";
                                }}
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>{category?.title}</td>
                          <td style={{ minWidth: "220px" }}>
                            {category?.description ? (
                              <div
                                className="description-preview"
                                dangerouslySetInnerHTML={{
                                  __html: getDescriptionPreviewHtml(category.description),
                                }}
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                          <td style={{ minWidth: "220px" }}>{formatPlans(category?.plan)}</td>
                          <td>{category?.testCount || 0}</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleStatus(category?._id, category?.status)}
                              className={
                                category?.status === "active"
                                  ? "btn btn-pill btn-primary btn-sm"
                                  : "btn btn-pill btn-danger btn-sm"
                              }
                            >
                              <span>{category?.status === "active" ? "Active" : "InActive"}</span>
                            </button>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <Link className="action-btn edit-btn" to={`/daily-mock-categories/edit/${category?._id}`}>
                                <i className="fa fa-edit" />
                              </Link>
                              <button
                                type="button"
                                className="action-btn edit-btn bg-danger"
                                onClick={() => handleDelete(category?._id)}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .sequence-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sequence-btn {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 50%;
          background: #f3f4f6;
          color: #374151;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sequence-btn.disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .sequence-number {
          min-width: 32px;
          text-align: center;
          font-weight: 700;
        }

        .description-preview {
          max-width: 360px;
          color: #374151;
          font-size: 14px;
          line-height: 1.5;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          word-break: break-word;
        }

        .description-preview p,
        .description-preview div,
        .description-preview span,
        .description-preview strong,
        .description-preview em,
        .description-preview li,
        .description-preview ul,
        .description-preview ol {
          margin: 0;
          padding: 0;
          font-size: inherit !important;
          line-height: inherit !important;
        }

        .description-preview ul,
        .description-preview ol {
          list-style-position: inside;
        }
      `}</style>
    </section>
  );
}
